import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import filter from "lodash/filter"
import { Component } from "react"
import PropTypes from "prop-types"
import { getWindow } from "utils-lib/getWindow"
import getFacilityMapMarkers from "component-lib/AEM/FacilityLocatorComponent/utils/getFacilityMapMarkers"
import Box from "component-lib/Base/components/Box"
import { Typography } from "component-lib/Base/components/Typography/Typography"
import getContentUrl from "utils-lib/getContentUrl"
import { isNextApp } from "utils-lib/isNextApp"
import withHydration from "hoc-lib/hydration"
import MapComponent, {
  MapComponentSkeleton,
} from "component-lib/AEM/FacilityLocatorComponent/MapComponent/MapComponent"
import FacilitySearch from "component-lib/AEM/FacilityLocatorComponent/FacilitySearch/FacilitySearchContainer"
import { FacilitySearchSkeleton } from "component-lib/AEM/FacilityLocatorComponent/FacilitySearch/FacilitySearch"

import "./Map.scss"

const ssrWindow = getWindow()

class MapUnwrapped extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dropoffType: `drop_off_waste`,
      hasGoogleMapsLoaded: false,
    }
    this.errorMessage = ``
  }

  componentDidUpdate() {
    // once hydration is complete, we can check if google maps has loaded
    // this is required before we can render the map component
    // this check properly handles the case where the map is not loaded yet (SSR)
    // only doing this for the Next JS app, so there is no need to add a feature flag
    if (isNextApp && this.props.isReady && !this.state.hasGoogleMapsLoaded) {
      const hasGoogleMapsLoaded = ssrWindow.google?.maps !== undefined
      if (hasGoogleMapsLoaded && !this.state.hasGoogleMapsLoaded) {
        this.setState({ hasGoogleMapsLoaded: true })
      }
    }
  }

  onToggleDropoffType = () => {
    if (this.state.dropoffType === `drop_off_waste`) {
      this.setState({ dropoffType: `drop_off_recycling` })
    } else {
      this.setState({ dropoffType: `drop_off_waste` })
    }
  }

  onFacilityListItemClick = (id) => {
    const { language, selectFacility, translateFacility } = this.props

    selectFacility({
      hoveredMapMarkerId: null,
      selectedFacilityId: id,
      listItemSelected: true,
    })

    // If French, use google translate.
    translateFacility(language, id)
  }

  // When a map marker is clicked, update the icon to be in it's hovered state
  // and show the details of the selected facility.
  // Do not zoom in or recenter the map.
  onMapMarkerClick = (id) => {
    const { selectFacility, translateFacility } = this.props
    const { language } = this.props
    selectFacility({
      hoveredMapMarkerId: id,
      selectedFacilityId: id,
      listItemSelected: false,
    })
    // If French, use google translate.
    translateFacility(language, id)
  }

  customErrorMessage = (value) => {
    this.errorMessage = value
  }

  renderSearch() {
    const {
      facilityLocator,
      clearSelectedFacilityId,
      clearhoveredMapMarkerId,
      getMapLocations,
      setHasGeosuggestion,
      setSearchText,
      setCustomerAction,
      sethoveredMapMarkerId,
      updateGeosuggestLocation,
      setCustomerAddress,
      isReady,
    } = this.props

    const facilitySearchProps = {
      clearSelectedFacilityId,
      getMapLocations,
      setHasGeosuggestion,
      setSearchText,
      setCustomerAction,
      updateGeosuggestLocation,
      onMouseEnterItem: sethoveredMapMarkerId,
      onMouseLeaveItem: clearhoveredMapMarkerId,
      onFacilityListItemClick: this.onFacilityListItemClick,
      map: facilityLocator,
      onToggleDropoffType: this.onToggleDropoffType,
      dropoffType: this.state.dropoffType,
      customErrorMessage: this.customErrorMessage,
      isAccordionItem: false,
      zipcode: this.props.zipcode,
      setCustomerAddress,
      hasMapLocations:
        !isEmpty(
          filter(get(facilityLocator, `mapLocations`, []), (loc) =>
            this.state.dropoffType === `drop_off_waste`
              ? loc.properties.facTypeCode === `2` ||
                loc.properties.facTypeCode === `4`
              : loc.properties.facTypeCode === `3`,
          ),
        ) && get(facilityLocator, `hasLoadedLocations`, false),
    }

    if (!isReady && isNextApp) {
      // we only need a skeleton loader for the facility search component if we are in the Next JS app
      return <FacilitySearchSkeleton />
    }

    return <FacilitySearch key="map" {...facilitySearchProps} />
  }

  renderMapComponent() {
    const {
      facilityLocator,
      siteLanguage,
      sethoveredMapMarkerId,
      clearhoveredMapMarkerId,
      defaultMapCenter,
    } = this.props

    const { showErrorMessage } = this.props.customerSelections

    const { hasGoogleMapsLoaded } = this.state

    const mapProps = {
      hoveredMapMarkerId: facilityLocator?.hoveredMapMarkerId,
      selectedMapMarkerId: facilityLocator?.selectedFacilityId,
      mapMarkers: getFacilityMapMarkers(
        get(facilityLocator, `mapLocations`, []),
        this.state.dropoffType,
        facilityLocator?.selectedFacilityId,
        facilityLocator?.hoveredMapMarkerId,
        facilityLocator?.listItemSelected,
        get(facilityLocator, `alerts`, []),
      ),
      onMapMarkerClick: this.onMapMarkerClick,
      onMouseEnterItem: sethoveredMapMarkerId,
      onMouseLeaveItem: clearhoveredMapMarkerId,
      errorMessage: this.errorMessage,
      userLocationCoordinates:
        facilityLocator?.selectedGeosuggestion &&
        facilityLocator?.selectedGeosuggestion.lat !== null &&
        facilityLocator?.selectedGeosuggestion.lng !== null
          ? {
              lat: facilityLocator?.selectedGeosuggestion.lat,
              lng: facilityLocator?.selectedGeosuggestion.lng,
            }
          : null,
      hasSuggestion: !showErrorMessage,
      height: `26rem`,
      siteLanguage,
      defaultMapCenter,
      hasMapLocations:
        !isEmpty(
          filter(get(facilityLocator, `mapLocations`, []), (loc) =>
            this.state.dropoffType === `drop_off_waste`
              ? loc.properties.facTypeCode === `2` ||
                loc.properties.facTypeCode === `4`
              : loc.properties.facTypeCode === `3`,
          ),
        ) && get(facilityLocator, `hasLoadedLocations`, false),
    }

    if (!isEmpty(defaultMapCenter)) {
      mapProps.defaultMapZoom = 9
      mapProps.defaultShowAllMapZoom = 10.5
    }

    const hasLocations =
      !isEmpty(get(facilityLocator, `mapLocations`, [])) &&
      get(facilityLocator, `hasLoadedLocations`, false)
    const style = {
      flexGrow: 1,
      height: `100%`,
      width: `100%`,
      maxWidth: `100%`,
      transition: `max-width .25s linear .5s`,
    }

    if (hasLocations) {
      style.maxWidth = `48.5%`
      style.transition = `max-width .25s linear`
    }

    // not using a feature flag for this because the isNextApp check is like a feature flag
    // and I don't want to add another layer of complexity
    // we can disable pages using this component in the Next JS app with a feature flag if needed
    if (!hasGoogleMapsLoaded && isNextApp) {
      // we only need a skeleton loader for the map component if we are in the Next JS app
      return <MapComponentSkeleton />
    }

    return (
      <MapComponent key={`map-${ssrWindow.location.pathname}`} {...mapProps} />
    )
  }

  render() {
    const {
      language,
      heading,
      isHeadingH1,
      description,
      icon,
      altText,
      componentId,
      // userAccount,
    } = this.props

    return heading ? (
      <div className="Map pt-5" data-testid="MapUnwrapped" id={componentId}>
        <div className="container pt-4 pt-md-0">
          <div className="title d-flex align-items-md-center flex-column flex-lg-row py-3">
            <div className="d-flex">
              <img
                className={`${
                  language === `fr_CA` ? `title_icon_fr` : `title_icon_en`
                }`}
                src={getContentUrl(icon)}
                alt={altText}
              />
              <Box
                className="d-flex align-items-center"
                style={{
                  maxWidth: `40rem`,
                  minHeight: language === `fr_CA` ? `4rem` : `4.5rem`,
                }}
              >
                <Typography
                  className={`${language} ${
                    language === `fr_CA` ? `text-normal` : `text-nowrap`
                  } title_heading flex-grow-2 maax-font-weight-black pl-3 pr-5 `}
                  component={isHeadingH1 ? "h1" : "h2"}
                  variant="h2"
                  style={{
                    fontSize: language === `fr_CA` ? `2.5rem` : `3rem`,
                  }}
                  data-testid="MapUnwrapped-Heading"
                >
                  {heading}
                </Typography>
              </Box>
            </div>
            <div
              className="wm-text-gray-dark flex-grow-1"
              style={{
                fontSize: `18px`,
              }}
              data-testid="MapUnwrapped-Description"
            >
              {description}
            </div>
          </div>
        </div>
        <div className="d-flex flex-wrap desktop container">
          {this.renderSearch()}
          {this.renderMapComponent()}
        </div>
      </div>
    ) : null
  }
}

MapUnwrapped.propTypes = {
  // reduxState
  customerSelections: PropTypes.object.isRequired,
  facilityLocator: PropTypes.object.isRequired,
  siteLanguage: PropTypes.object.isRequired,
  userAccount: PropTypes.object.isRequired,
  isReady: PropTypes.bool.isRequired,
  // reduxActions
  clearSelectedFacilityId: PropTypes.func.isRequired,
  clearhoveredMapMarkerId: PropTypes.func.isRequired,
  getMapLocations: PropTypes.func.isRequired,
  selectFacility: PropTypes.func.isRequired,
  setHasGeosuggestion: PropTypes.func.isRequired,
  setSearchText: PropTypes.func.isRequired,
  setCustomerAction: PropTypes.func.isRequired,
  sethoveredMapMarkerId: PropTypes.func.isRequired,
  updateGeosuggestLocation: PropTypes.func.isRequired,
  translateFacility: PropTypes.func.isRequired,
  setCustomerAddress: PropTypes.func.isRequired,
  // From localization context
  strings: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  // AEM_DEV_NOTE: From aem JSON
  heading: PropTypes.string.isRequired,
  isHeadingH1: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.string.isRequired,
  altText: PropTypes.string,
  defaultMapCenter: PropTypes.object.isRequired,
  zipcode: PropTypes.string.isRequired,
  componentId: PropTypes.string.isRequired,
}

MapUnwrapped.defaultProps = {
  description: ``,
  altText: ``,
}

export default withHydration(MapUnwrapped)
